import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DND5eHPCalculator = () => {
  const [characterClass, setCharacterClass] = useState('');
  const [level, setLevel] = useState('');
  const [constitutionModifier, setConstitutionModifier] = useState('');
  const [multiclass, setMulticlass] = useState(false);
  const [additionalClasses, setAdditionalClasses] = useState([]);
  const [toughFeat, setToughFeat] = useState(false);
  const [hillDwarf, setHillDwarf] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const hitDice = {
    barbarian: 12,
    bard: 8,
    cleric: 8,
    druid: 8,
    fighter: 10,
    monk: 8,
    paladin: 10,
    ranger: 10,
    rogue: 8,
    sorcerer: 6,
    warlock: 8,
    wizard: 6
  };

  const hitDiceAverages = {
    4: 2.5,
    6: 3.5,
    8: 4.5,
    10: 5.5,
    12: 6.5
  };

  const handleClassChange = (e) => {
    setCharacterClass(e.target.value);
    setError('');
  };

  const handleLevelChange = (e) => {
    setLevel(e.target.value);
    setError('');
  };

  const handleConstitutionModifierChange = (e) => {
    setConstitutionModifier(e.target.value);
    setError('');
  };

  const handleMulticlassChange = (e) => {
    setMulticlass(e.target.checked);
  };

  const handleAdditionalClassChange = (index, field, value) => {
    const updatedClasses = [...additionalClasses];
    updatedClasses[index][field] = value;
    setAdditionalClasses(updatedClasses);
  };

  const handleAddClass = () => {
    setAdditionalClasses([...additionalClasses, { class: '', level: '' }]);
  };

  const handleRemoveClass = (index) => {
    const updatedClasses = additionalClasses.filter((_, i) => i !== index);
    setAdditionalClasses(updatedClasses);
  };

  const handleToughFeatChange = (e) => {
    setToughFeat(e.target.checked);
  };

  const handleHillDwarfChange = (e) => {
    setHillDwarf(e.target.checked);
  };

  const calculateHP = () => {
    if (!characterClass || !level || !constitutionModifier) {
      setError('Please fill in all required fields');
      return;
    }

    if (isNaN(level) || isNaN(constitutionModifier)) {
      setError('Please enter valid numbers for level and constitution modifier');
      return;
    }

    const numericLevel = parseInt(level);
    const numericConstitutionModifier = parseInt(constitutionModifier);

    if (numericLevel < 1) {
      setError('Level must be at least 1');
      return;
    }

    let totalHP = 0;
    let calculationSteps = [];

    // Calculate base HP from primary class
    const primaryClassHitDie = hitDice[characterClass.toLowerCase()];
    const primaryClassHitDieAverage = hitDiceAverages[primaryClassHitDie];

    // First level always gets maximum hit die
    totalHP += primaryClassHitDie;
    calculationSteps.push(`Level 1: ${primaryClassHitDie} (max hit die)`);

    // Add constitution modifier for each level
    for (let i = 2; i <= numericLevel; i++) {
      totalHP += primaryClassHitDieAverage + numericConstitutionModifier;
      calculationSteps.push(`Level ${i}: ${primaryClassHitDieAverage} (avg hit die) + ${numericConstitutionModifier} (CON)`);
    }

    // Add multiclass HP
    if (multiclass && additionalClasses.length > 0) {
      additionalClasses.forEach((additionalClass, index) => {
        const additionalClassHitDie = hitDice[additionalClass.class.toLowerCase()];
        const additionalClassHitDieAverage = hitDiceAverages[additionalClassHitDie];
        const additionalClassLevel = parseInt(additionalClass.level);

        for (let i = 1; i <= additionalClassLevel; i++) {
          if (i === 1) {
            totalHP += additionalClassHitDie;
            calculationSteps.push(`Multiclass Level 1 (${additionalClass.class}): ${additionalClassHitDie} (max hit die)`);
          } else {
            totalHP += additionalClassHitDieAverage + numericConstitutionModifier;
            calculationSteps.push(`Multiclass Level ${i} (${additionalClass.class}): ${additionalClassHitDieAverage} (avg hit die) + ${numericConstitutionModifier} (CON)`);
          }
        }
      });
    }

    // Add Tough feat bonus
    if (toughFeat) {
      const toughBonus = numericLevel * 2;
      totalHP += toughBonus;
      calculationSteps.push(`Tough Feat: +${toughBonus} HP`);
    }

    // Add Hill Dwarf bonus
    if (hillDwarf) {
      const hillDwarfBonus = numericLevel;
      totalHP += hillDwarfBonus;
      calculationSteps.push(`Hill Dwarf: +${hillDwarfBonus} HP`);
    }

    // Create visualization data
    const classLabels = [characterClass];
    const classHP = [totalHP];
    additionalClasses.forEach((additionalClass) => {
      classLabels.push(additionalClass.class);
      classHP.push(totalHP);
    });

    const chartData = {
      labels: classLabels,
      datasets: [{
        label: 'Hit Points',
        data: classHP,
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      }]
    };

    setResults({
      totalHP,
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    characterClass: 'Barbarian',
    level: '5',
    constitutionModifier: '3',
    multiclass: false,
    toughFeat: true,
    hillDwarf: false,
    totalHP: 45
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>D&D 5e HP Calculator | Dungeons & Dragons Hit Points Tool</title>
          <meta name="description" content="Calculate your character's hit points in D&D 5e. This calculator helps determine maximum HP based on class, level, constitution modifier, and other bonuses." />
          <meta name="keywords" content="D&D 5e HP calculator, hit points calculator, D&D character calculator, D&D tools" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">🎲</div>
            <h1 className="text-3xl font-bold text-gray-800">D&D 5e HP Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate your character's hit points in D&D 5e. This calculator helps determine maximum HP based on class, level, constitution modifier, and other bonuses.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Character Class:</label>
              <select
                value={characterClass}
                onChange={handleClassChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="">Select a class</option>
                <option value="Barbarian">Barbarian</option>
                <option value="Bard">Bard</option>
                <option value="Cleric">Cleric</option>
                <option value="Druid">Druid</option>
                <option value="Fighter">Fighter</option>
                <option value="Monk">Monk</option>
                <option value="Paladin">Paladin</option>
                <option value="Ranger">Ranger</option>
                <option value="Rogue">Rogue</option>
                <option value="Sorcerer">Sorcerer</option>
                <option value="Warlock">Warlock</option>
                <option value="Wizard">Wizard</option>
              </select>
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Level:</label>
              <input
                type="number"
                value={level}
                onChange={handleLevelChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter character level"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Constitution Modifier:</label>
              <input
                type="number"
                value={constitutionModifier}
                onChange={handleConstitutionModifierChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter constitution modifier"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 flex items-center">
                <input
                  type="checkbox"
                  checked={multiclass}
                  onChange={handleMulticlassChange}
                  className="form-checkbox h-5 w-5 text-pink-600 mr-2"
                />
                Multiclass Character
              </label>
            </div>

            {multiclass && (
              <div className="mt-4">
                {additionalClasses.map((_, index) => (
                  <div key={index} className="flex flex-col md:flex-row gap-4 mb-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Additional Class:</label>
                      <select
                        value={additionalClasses[index].class}
                        onChange={(e) => handleAdditionalClassChange(index, 'class', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      >
                        <option value="">Select a class</option>
                        <option value="Barbarian">Barbarian</option>
                        <option value="Bard">Bard</option>
                        <option value="Cleric">Cleric</option>
                        <option value="Druid">Druid</option>
                        <option value="Fighter">Fighter</option>
                        <option value="Monk">Monk</option>
                        <option value="Paladin">Paladin</option>
                        <option value="Ranger">Ranger</option>
                        <option value="Rogue">Rogue</option>
                        <option value="Sorcerer">Sorcerer</option>
                        <option value="Warlock">Warlock</option>
                        <option value="Wizard">Wizard</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Level:</label>
                      <input
                        type="number"
                        value={additionalClasses[index].level}
                        onChange={(e) => handleAdditionalClassChange(index, 'level', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        placeholder="Enter class level"
                      />
                    </div>
                    <button
                      onClick={() => handleRemoveClass(index)}
                      className="mt-2 md:mt-0 md:mt-8 py-2 px-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200"
                    >
                      Remove Class
                    </button>
                  </div>
                ))}
                <button
                  onClick={handleAddClass}
                  className="mt-2 py-2 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200"
                >
                  Add Another Class
                </button>
              </div>
            )}

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 flex items-center">
                <input
                  type="checkbox"
                  checked={toughFeat}
                  onChange={handleToughFeatChange}
                  className="form-checkbox h-5 w-5 text-pink-600 mr-2"
                />
                Has Tough Feat
              </label>
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 flex items-center">
                <input
                  type="checkbox"
                  checked={hillDwarf}
                  onChange={handleHillDwarfChange}
                  className="form-checkbox h-5 w-5 text-pink-600 mr-2"
                />
                Is Hill Dwarf
              </label>
            </div>
          </div>

          <button
            onClick={calculateHP}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Hit Points
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Character Information</h3>
                  <p className="text-lg">Class: {characterClass}</p>
                  <p className="text-lg">Level: {level}</p>
                  <p className="text-lg">Constitution Modifier: {constitutionModifier}</p>
                  <p className="text-lg">Multiclass: {multiclass ? 'Yes' : 'No'}</p>
                  <p className="text-lg">Tough Feat: {toughFeat ? 'Yes' : 'No'}</p>
                  <p className="text-lg">Hill Dwarf: {hillDwarf ? 'Yes' : 'No'}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Hit Points Calculation</h3>
                  <p className="text-lg font-bold mt-4">Total Hit Points: {results.totalHP}</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Hit Points'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Classes'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Hit Points by Class'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for a 5th-level barbarian with a constitution modifier of +3 and the Tough feat:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Character Class: {exampleData.characterClass}</li>
                <li>Level: {exampleData.level}</li>
                <li>Constitution Modifier: {exampleData.constitutionModifier}</li>
                <li>Multiclass: {exampleData.multiclass ? 'Yes' : 'No'}</li>
                <li>Tough Feat: {exampleData.toughFeat ? 'Yes' : 'No'}</li>
                <li>Hill Dwarf: {exampleData.hillDwarf ? 'Yes' : 'No'}</li>
                <li>Total Hit Points: {exampleData.totalHP}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Start with maximum hit die for level 1</li>
                <li>Add average hit die and constitution modifier for levels 2-5</li>
                <li>Add Tough feat bonus (2 HP per level)</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the D&D 5e HP Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Character Information:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Select your character's class</li>
                  <li>Enter your character's level</li>
                  <li>Enter your character's constitution modifier</li>
                </ul>
              </li>
              <li><strong>Multiclass (Optional):</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Check the multiclass box</li>
                  <li>Add additional classes and their levels</li>
                </ul>
              </li>
              <li><strong>Add Bonuses (Optional):</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Check if your character has the Tough feat</li>
                  <li>Check if your character is a Hill Dwarf</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>Your character's total hit points</li>
                  <li>Detailed calculation steps</li>
                  <li>Visualization of hit points by class</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the total calculated hit points</li>
                  <li>Check the calculation steps for accuracy</li>
                  <li>View the visual representation of hit points</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>For higher accuracy, use the maximum hit die for level 1</li>
                <li>Average hit die values are used for levels beyond the first</li>
                <li>Always double-check with your Dungeon Master for house rules</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Hit Points in D&D 5e <span className="text-blue-600">🎲</span></h2>
          <div className="text-gray-600">
            <p>
              Hit points (HP) in D&D 5e represent your character's ability to withstand damage. They are determined by:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Hit Dice:</strong> Based on your character's class (e.g., Barbarians use d12, Wizards use d6)</li>
              <li><strong>Constitution Modifier:</strong> Added to HP each time you level up</li>
              <li><strong>Class Features:</strong> Some classes gain additional HP at certain levels</li>
              <li><strong>Racial Bonuses:</strong> Hill Dwarves gain extra HP per level</li>
              <li><strong>Feats:</strong> The Tough feat adds 2 HP per level</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Double-Check:</strong> Always verify calculations with your Dungeon Master</li>
              <li><strong>Plan Ahead:</strong> Calculate HP during character creation to avoid delays</li>
              <li><strong>Update Regularly:</strong> Recalculate HP each time you level up</li>
              <li><strong>Consider House Rules:</strong> Some campaigns may have custom HP rules</li>
            </ul>
            <p className="italic mt-4">
              Note: This calculator uses standard D&D 5e rules. Always confirm with your Dungeon Master if there are any campaign-specific adjustments.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DND5eHPCalculator;